import {LeaderLineLabeler} from "@esWebAPI/es/map/module/LeaderLineLabeler";
import {PointLayer} from "@esWebAPI/es/map/layer/PointLayer";
import {ThematicStylerFactory} from "@esWebAPI/es/map/styler/ThematicStylerFactory";
import {PolygonLayer} from "@esWebAPI/es/map/layer/PolygonLayer";
import {HeatMapLayer} from "@esWebAPI/es/map/layer/HeatMapLayer";
import {LineLayer} from "@esWebAPI/es/map/layer/LineLayer";
import {PointAndPolygonLayer} from "@esWebAPI/es/map/layer/analysis/PointAndPolygonLayer";

import type {Map} from "azure-maps-control";
import type {AzureImageSpriteManager} from "../../layer/AzureImageSpriteManager";
import type {AzureBaseMap} from "../AzureBaseMap";
import type {ILayerView, LayerInfo} from "@esWebAPI/es/model/Layer";
import type {IEventChannel, InitMapParams} from "@esWebAPI/es/model/Models";
import type {FeatureViewModel} from "@esWebAPI/es/map/FeatureViewModel";
import type {AzureOverlay} from "@esWebAPI/es/map/module/AzureOverlay";
import type LayerViewModel from "@esWebAPI/es/map/LayerViewModel";
import type {IMapLayer} from "@esWebAPI/es/map/layer/MapLayer";

export class Render {

    protected readonly baseMap: AzureBaseMap;
    protected readonly featureViewModel: FeatureViewModel;
    protected readonly analysis: any;
    protected readonly _map: Map;
    protected readonly eventChannel;
    protected readonly spriteManager: AzureImageSpriteManager;
    protected readonly initMapParams: InitMapParams;
    protected readonly overlayModule: AzureOverlay;
    protected llLabeler: LeaderLineLabeler|undefined;

    protected _loadComplete = $.Deferred();

    protected _isRefreshing: boolean = false;

    protected stylerFactory: ThematicStylerFactory;

    protected layerGroupSorter: ((layerA: IMapLayer, layerB: IMapLayer) => number)|undefined;

    constructor(
        baseMap: AzureBaseMap,
        featureViewModel: FeatureViewModel,
        analysis: any,
        initMapParams: InitMapParams,
        overlayModule: AzureOverlay,
        stylerFactory: ThematicStylerFactory,
        eventChannel: IEventChannel
    ) {
        this.featureViewModel = featureViewModel;
        this.baseMap = baseMap;
        this.analysis = analysis;
        this._map = baseMap.map;
        this.eventChannel = eventChannel;
        this.spriteManager = baseMap.imageSpriteManager;
        this.initMapParams = initMapParams;
        this.overlayModule = overlayModule;
        this.stylerFactory = stylerFactory;
    }

    private initListeners() {
        this.eventChannel.addListener('onAddLayer', (layer: LayerViewModel) => this.onAddLayer(layer.getLayerView()), this);
        this.eventChannel.addListener('onRemoveLayer', (layer: LayerViewModel) => this.onRemoveLayer(layer.getLayerView()), this);
        this.eventChannel.addListener("onUpdateLayer", (layer: LayerViewModel) => this.onUpdateLayer(layer.getLayerView()), this);
        this.eventChannel.addListener("onUpdateLayerIndex", this.onUpdateLayerIndex, this);
        this.eventChannel.addListener("onSetBaseMap", () => {
            const mapLibre = this.baseMap.getMapLibreMap();
            const POLL_INTERVAL = 200;
            const TIMEOUT = 5000;
            let elapsed = 0;

            const waitForStyleThenReorder = () => {
                if (mapLibre.isStyleLoaded()) {
                    // Style loaded - short delay to let Azure Maps finish internal layer operations
                    window.setTimeout(() => {
                        this.reorderLayers();
                    }, 300);
                } else if (elapsed < TIMEOUT) {
                    elapsed += POLL_INTERVAL;
                    window.setTimeout(waitForStyleThenReorder, POLL_INTERVAL);
                } else {
                    // Timeout fallback - reorder anyway
                    this.reorderLayers();
                }
            };

            waitForStyleThenReorder();
        });
        this.eventChannel.addListener("onSyncDatasetFinishAndFeatureViewModelUpdated", () => {
            this.reload();
        });
    }

    /**
     * Start loading the render.
     * Need to be called immediately after constructor and only once.
     */
    public startLoad() {
        this.baseMap.map.events.add('load', () => {
            (async () => {
                await this.baseMap.loadComplete;
                await this.load();
                this.initListeners();
            })();
        });

    }

    get map() {
        return this._map;
    }

    get loadComplete(){
        return this._loadComplete;
    }

    /**
     * @deprecated
     */
    get layers(): IMapLayer[] {
        return this.baseMap.layers;
    }

    protected async load(): Promise<void> {

        await Promise.all(this.featureViewModel.listLayers().map((lvModel: any) => {

            // @ts-ignore
            const layerConfig = this.initMapParams?.layers?.find(lvConfig => lvConfig.id === lvModel.getId());

            if (layerConfig?.enabled === false) {
                // Skip this layer if explicitly disabled
                return Promise.resolve();
            }

            if (layerConfig?.enabled !== true && lvModel.isEnabled() === false) {
                // Lazy load: don't create the layer and full datasource fetch for legend-disabled layers.
                return Promise.resolve();
            }

            return this.addLayer(lvModel.getLayerView());
        }));

        const dfds: JQuery.Deferred<void>[] = [];
        for (const layer of this.baseMap.layers) {
            dfds.push(layer.onReady)
        }

        $.when(...dfds).then(() => {
            this.llLabeler = new LeaderLineLabeler(this.baseMap, this.featureViewModel, this.overlayModule, this.eventChannel);
            this.llLabeler.init().then((_lllIds) => {
                this.reorderLayers();

                this.baseMap.map.events.add('layeradded', (layer) => {
                    if (this.baseMap.getLayerById(layer.getId())) {
                        es.log.log("layeradded", layer);
                        this.reorderLayers();
                    }
                });

                this._loadComplete.resolve();
            });
        });
    }

    /**
     * Reorder layers to ensure proper rendering order
     */
    protected reorderLayers() {

        //this.logLayerRenderOrder("reorderLayers before");

        // get all layers except azure/bing layers
        const appLayers = this.baseMap.map.layers.getLayers()
            .filter(layer => !!layer && typeof layer.getId === 'function')
            .map(layer => ({
                layer,
                id: layer.getId()
            }))
            .filter(l => l.id.indexOf('.bing.') < 0);

        // build a mapping of layerIds : layerView objects for user preference ordering
        const layerViewMapping: any = {};
        this.featureViewModel.listLayers().forEach(lvm => {
            const layerView = lvm.getLayerView();
            const layerViewId = layerView.id;

            layerViewMapping[layerViewId] = {
                ordering: lvm.getOrdering(),
                type: lvm.getType(),
                geomType: lvm.getGeomType(),
                layerView: layerView
            };
        });

        // set layer order from:
        //     top-to-bottom (groups)
        //     right-to-left (layers)
        // todo: make this private static readonly class prop?
        // todo: update these prefix refs to be public props? e.g. AzureOverlay.BUBBLE_LAYER_ID
        // todo: handle weather?
        const layerGroups = [
            {sort: 0, layers: ['lll_label_', 'lll_line_']}, // note: lll must on top of all other labels or there can be label collision issues
            //{sort: 0, layers: []},
            {sort: 1, layers: ['route_stop_symbol_layer_']},
            {sort: 1, layers: ['pieClusterLabel_', 'pieClusterBubble_']},
            {sort: 1, layers: ['label_']},
            {sort: 0, layers: ['overlay-bubble', 'overlay-symbol', 'overlay-line', 'overlay-polygon']},
            {sort: 0, layers: ['territory-label-layer']},
            {sort: 0, layers: ['dragpoint_handler', 'lasso-highlight-layer']},
            {sort: 1, layers: ['-selection']},
            {sort: 1, layers: ['symbol_', 'bubble_', 'symbol_and_polygon_']},
            {sort: 0, layers: ['hm_']},
            {sort: 1, layers: ['line_layer_', 'line_', 'polygon_']},
            {sort: 0, layers: ['territory-selection-layer', 'ter_outlines_layer_polygon', 'territory-base-layer-line', 'territory-base-layer']}
        ];

        let currentBeforeId = null;
        for (let i = 0; i < layerGroups.length; i++) {
            const layerGroup = layerGroups[i];
            let groupLayers = [];

            for (const idPattern of layerGroup.layers) {
                const matchingLayers = appLayers.filter(l =>
                    l.id === idPattern ||
                    idPattern.endsWith('_') && l.id.startsWith(idPattern) && !l.id.endsWith('-selection')||
                    (!idPattern.endsWith('_') && l.id.includes(idPattern))
                );

                groupLayers.push(...matchingLayers);
            }

            // handle groups that support user-preference sort order
            if (layerGroup.sort) {
                groupLayers.sort((a, b) => {
                    const layerA = this.baseMap.getLayerById(a.id);
                    const layerB = this.baseMap.getLayerById(b.id);

                    if (!layerA || !layerB) {
                        return 0;
                    }

                    if (this.layerGroupSorter) {
                        const ret = this.layerGroupSorter(layerA, layerB);
                        if (ret !== 0) {
                            return ret;
                        }
                    }

                    const aInfo = layerViewMapping[layerA.layerView.id];
                    const bInfo = layerViewMapping[layerB.layerView.id];

                    if (aInfo && bInfo) {
                        return bInfo.ordering - aInfo.ordering; // higher ordering = higher in stack
                    }

                    return 0;
                });
            }

            for (const layerInfo of groupLayers) {
                // for the first group (top of stack), we need to move without a reference
                if (currentBeforeId === null) {
                    this.baseMap.map.layers.move(layerInfo.id);
                } else {
                    // for subsequent groups, we can move before the current reference layer
                    this.baseMap.map.layers.move(layerInfo.id, currentBeforeId);
                }

                // update current reference layer id
                currentBeforeId = layerInfo.id;
            }
        }

        //this.logLayerRenderOrder("reorderLayers after");
    }

    private logLayerRenderOrder(msg: string | undefined){
        if(es.isDeveloper){
            const order = this.baseMap.getMapLibreMap().getStyle().layers
                .filter((l:any) => !l.id.includes(".bing."))
                .map((l:any) => l.id);
            if(msg){
                es.log.log(msg, order);
            }
            es.log.table(order);
        }
    }

    private async reloadLayer(layerViewId: number) {
        const layerView = this.featureViewModel.getLayerById(layerViewId).getLayerView();

        this.baseMap.removeLayer(layerViewId);

        await this.addLayer(layerView);

        const layer = this.baseMap.getLayersByLayerViewId(layerViewId)[0];
        if (!layer) {
            throw new Error("No layer found for layerViewId " + layerViewId);
        }


        //Show a loading spinner for PBF Layers
        const dsId = this.baseMap.getDatasourceManager().getDatasourceIds([layerView.id])[0];

        if (this.baseMap.getDatasourceManager().isPbf(dsId)) {
            this.eventChannel.fireEvent("onShowLoadingOverlay");
        }

        await layer.onReady;

        if (this.baseMap.getDatasourceManager().isPbf(dsId)) {
             this.eventChannel.fireEvent("onHideLoadingOverlay");
        }
    }

    // Guards against a second enable toggle while the first lazy load is still in progress.
    private lazyLoadingLayerIds = new Set<number>();

    /**
     * Create a layer that was skipped at load() because it was legend-disabled (lazy load).
     * Mirrors the reloadLayer() add path: addLayer + await onReady, with a loading overlay.
     */
    private async lazyAddLayer(layerView: ILayerView) {
        if (this.lazyLoadingLayerIds.has(layerView.id)) {
            return;
        }

        if (layerView.$type === "ScheduleLayerView") {
            // ScheduleLayerView can't be disabled so there is no need to lazyload
            return;
        }

        this.lazyLoadingLayerIds.add(layerView.id);
        this.eventChannel.fireEvent("onShowLoadingOverlay");
        try {
            await this.addLayer(layerView);

            const layer = this.baseMap.getLayersByLayerViewId(layerView.id)[0];
            if (layer) {
                await layer.onReady;

                // The user may have toggled the layer back off while it was loading. The layer was
                // built from the layerView captured at toggle-on, so re-check the live model.
                const lvModel = this.featureViewModel.getLayerById(layerView.id);
                if (lvModel && lvModel.isEnabled() === false) {
                    layer.hide();
                }
            }
        } finally {
            this.eventChannel.fireEvent("onHideLoadingOverlay");
            this.lazyLoadingLayerIds.delete(layerView.id);
        }
    }

    /**
     * Ensure the map layer for a layerView exists, lazily creating it if it was skipped at load()
     * (legend-disabled). For callers that show/hide layers directly, bypassing the enabled model
     * state - e.g. the mobile API's showSingleLayer. Resolves to the layer, or undefined if the
     * layerView is unknown.
     */
    public async ensureLayerLoaded(layerViewId: number): Promise<IMapLayer|undefined> {
        let layer = this.baseMap.getLayersByLayerViewId(layerViewId)[0];
        if (!layer) {
            const lvModel = this.featureViewModel.getLayerById(layerViewId);
            if (!lvModel) {
                return undefined;
            }
            await this.lazyAddLayer(lvModel.getLayerView());
            layer = this.baseMap.getLayersByLayerViewId(layerViewId)[0];
        }
        return layer;
    }

    protected async reload(reloadDataSourcesDeprecated: boolean = false) {
        if (reloadDataSourcesDeprecated) {
            es.log.warn("Render.reload called with reloadDataSources=true. Datasources should be destroyed in layer#destroy method and created/recreated in constructor");
        }

        this._loadComplete = $.Deferred();

        this.baseMap.removeAllLayer();

        this.llLabeler?.destroy();

        return this.load();
    }


    protected async addSymbolLayer(layerView: ILayerView, layerInfo: LayerInfo) {

        //Radius/DriveTime analysis need a special layer has it has GeometryCollection features (1 Point + 1 Polygon)
        if (this.analysis && ["WithinDistanceAnalysis", "DriveTimeAnalysis"].includes(this.analysis.$type) &&
            this.analysis.generatedAggregateLayerViewId === layerView.id) {

            this.baseMap.addLayer(new PointAndPolygonLayer(this._map, layerView, layerInfo,
                this.baseMap.getDatasourceManager(), this.spriteManager, this.stylerFactory, this.eventChannel));
            return;
        }
        this.baseMap.addLayer(new PointLayer(this._map, layerView, layerInfo,
            this.baseMap.getDatasourceManager(), this.spriteManager, this.stylerFactory, this.eventChannel));
    }

    protected addPolygonLayer(layerView: ILayerView, layerInfo: LayerInfo) {
        this.baseMap.addLayer(new PolygonLayer(this._map, layerView, layerInfo,
            this.baseMap.getDatasourceManager(), this.spriteManager, this.stylerFactory, this.eventChannel));
    }

    protected addLineLayer(layerView: ILayerView, layerInfo: LayerInfo) {
        this.baseMap.addLayer(new LineLayer(this._map, layerView, layerInfo,
            this.baseMap.getDatasourceManager(), this.stylerFactory, this.eventChannel));
    }

    protected async addHeatMapLayer(layerView: ILayerView, existingLayerInfo: LayerInfo) {
        let layerInfo = existingLayerInfo;

        const attr = layerView.styleSettings.heatMapSettings?.attr;
        if (attr && !layerInfo.featureInfo.attributeRanges[attr]) {
            layerInfo = await es.api.layerInfo.getForLayerView(layerView.id, [attr]);
        }

        const layer = new HeatMapLayer(this._map, layerView, layerInfo,
            this.baseMap.getDatasourceManager(), this.spriteManager, this.stylerFactory, this.eventChannel);
        this.baseMap.addLayer(layer);
    }

    public async addLayer(layerView: ILayerView) {
        const layerInfo = this.featureViewModel.getLayerById(layerView.id).getLayerInfo();

        switch(layerView.geomType) {
            case "POINT" :
                if (layerView.styleSettings?.heatMapSettings?.enabled) {
                    await this.addHeatMapLayer(layerView, layerInfo)
                } else {
                    await this.addSymbolLayer(layerView, layerInfo);
                }
                break;
            case "POLYGON" :
                this.addPolygonLayer(layerView, layerInfo);
                break;
            case "LINESTRING" :
                this.addLineLayer(layerView, layerInfo);
                break;
            default:
                throw new Error("addLayer layerView.geomType not supported:" + layerView.geomType);
        }
    }

    protected onAddLayer(layerView: ILayerView) {
        if(!layerView.id){
            console.warn("layerViewModel class is being passed where an layerView object is expected", layerView)
            // @ts-expect-error
            layerView = layerView.getLayerView();
        }
        if (this.lazyLoadingLayerIds.has(layerView.id)) {
            return;
        }

        this.addLayer(layerView);
    }

    private onRemoveLayer(layerView: ILayerView) {
        this.baseMap.removeLayer(layerView.id);
    }

    protected onUpdateLayerIndex() {
        this.reorderLayers();
    }

    private async onUpdateLayer(layerView: ILayerView) {

        if (!this.baseMap.getLayersByLayerViewId(layerView.id)[0]) {
            // Layer was skipped at load() because it was legend-disabled (lazy load).
            // Create it on first enable, all later updates find it via the normal path.
            if (layerView.enabled) {
                await this.lazyAddLayer(layerView);
            }
            return;
        }

        if (layerView.geomType !== "POINT") {
            return;
        }

        const layer = this.baseMap.getLayersByLayerViewId(layerView.id)[0]?.layerView;
        if (!layer) {
            return;
        }

        // if there was a heat map and no longer exists/is enabled (or vice-versa), reload layer to use different class (SymbolLayer/HeatMapLayer)
        const currentSettings = layerView.styleSettings;
        const newSettings = layer.styleSettings;

        const isHeatMapChanges =
            (currentSettings?.heatMapSettings?.enabled !== newSettings?.heatMapSettings?.enabled) ||
            (currentSettings?.heatMapSettings?.attr !== newSettings?.heatMapSettings?.attr);

        if (isHeatMapChanges) {
            es.log.log("Detected isHeatMapChanges which require reloading the layer", layer.title);
            await this.reloadLayer(layer.id);
        }
    }

    /**
     * Refresh the render to reflect any change in layers/features
     * Used for mobile App, (eg. When an address point is added through the API)
     */
    public async refresh(): Promise<void> {
        const geoMapId = this.featureViewModel.geoMapId;

        if (this._isRefreshing) {
            return;
        }
        this._isRefreshing = true;

        const lvs = await es.api.layerView.getByGeoMap(geoMapId, {noCache: true});

        //Check if a layer was removed (present in baseMap but not in fetched lvs)
        this.baseMap.layers.forEach(l => {
            if (lvs.filter( lv => lv.id == l.layerView.id).length == 0) {
                // Layer was removed
                this.featureViewModel.removeLayer(l.layerView.id);
                this.baseMap.removeLayer(l.layerView.id);
            }
        });

        //Check if a layer was added (present in fetched lvs but not in baseMap )
        const toAdd: ILayerView[] = lvs.filter(lv => !this.baseMap.getLayersByLayerViewId(lv.id)[0]);
        for (const lv of toAdd) {
            const layerInfo = await es.api.layerInfo.getForLayerView(lv.id, undefined, {suppressDefaultErrorHandling: true});
            await this.featureViewModel.addLayerView(lv, layerInfo, true)
        }

        // If at least one layer was added just reload everything
        if (toAdd.length > 0) {
            try {
                await this.reload();
                this._isRefreshing = false;
            } catch (e: any) {
                es.log.error("Render#refresh : error reloading layers", e);
            }
            return;
        }

        //Otherwise update layer and layerInfo then reload the datasource
        const promises: Promise<void>[] = [];
        this.baseMap.layers.forEach(l => {
            const layerPromise = new Promise<void>((resolveLayer) => {
                (async () => {
                    await es.api.layerInfo.getForLayerView(l.layerView.id, undefined,
                        {suppressDefaultErrorHandling: true, noCache: true});
                    await this.reloadLayer(l.layerView.id);
                    resolveLayer()
                })()
            });
            promises.push(layerPromise);
        });

        // Use allSettled to handle all promise outcomes
        await Promise.allSettled(promises);
        this._isRefreshing = false;
    }

    public destroy() {
        this.baseMap.removeAllLayer();
    }
}
